import type {ImmersivePlugin, NavigationItem, PluginComponent} from '@github-ui/copilot-chat/plugin'
import {AgentsViewWrapper} from './components/agents-view/AgentsView'
import {AGENTS_PLUGIN_ID, AGENTS_PATH, AGENTS_PLUGIN_NAME} from './utils/constants'
import {AgentIcon} from '@primer/octicons-react'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {COPILOT_PATH} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {CopilotChatState} from '@github-ui/copilot-chat/utils/copilot-chat-reducer'
import {ModeSupportName} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {useNavigate} from '@github-ui/use-navigate'
import {submitTask} from './utils/submit-task'
import type {RefObject} from 'react'
import type {HandleUserSubmit} from './types/user-submit'
import {SlashCommandModelPicker} from './components/slash-command-model-picker/SlashCommandModelPicker'
import type {SelectedAgent} from './types/session'

const AGENTS_REGEX = /\/copilot\/agents/
export const COPILOT_AGENTS_PATH = `${COPILOT_PATH}/agents`

export class AgentsPlugin implements ImmersivePlugin {
  id = AGENTS_PLUGIN_ID

  displayName = AGENTS_PLUGIN_NAME

  constructor(
    createTaskFromInput: boolean = false,
    handleUserSubmitRef?: RefObject<HandleUserSubmit>,
    agentsEnabled: boolean = false,
  ) {
    this.createTaskFromInput = createTaskFromInput
    this.handleUserSubmitRef = handleUserSubmitRef
    this.agentsEnabled = agentsEnabled
  }

  createTaskFromInput: boolean

  handleUserSubmitRef: RefObject<HandleUserSubmit> | undefined

  agentsEnabled: boolean

  skipSyncRouteToThread = true

  components: PluginComponent[] = [
    {
      id: 'agents-view',
      Component: AgentsViewWrapper,
      routes: [AGENTS_REGEX],
    },
  ]

  slashCommand = {
    key: 'agent',
    name: 'Agent',
    description: 'Give Copilot cloud agent a background task',
    icon: AgentIcon,
    order: 1,
    enabled: () => this.agentsEnabled,
    disabledAction: (navigate: ReturnType<typeof useNavigate>) => {
      navigate(COPILOT_AGENTS_PATH, {preventTurbo: true})
    },
    execute: async (params: string, chatState: CopilotChatState, navigate: ReturnType<typeof useNavigate>) => {
      // If this is an existing chat thread, we want to send a chat message instead of navigating to the agents page.
      if (
        this.agentsEnabled &&
        chatState.selectedThreadID &&
        isFeatureEnabled('copilot_immersive_task_within_chat_thread')
      ) {
        return false
      }

      // If agents are enabled and we can create tasks directly, do that
      if (this.agentsEnabled && this.createTaskFromInput && this.handleUserSubmitRef) {
        submitTask(params, this.handleUserSubmitRef)
        return true
      }

      // Otherwise, always navigate to agents page (for disabled agents or when we can't create tasks directly)
      copilotLocalStorage.setTaskEntrypointMessage(params)
      navigate(COPILOT_AGENTS_PATH, {preventTurbo: true})
      return true
    },
    suggestions: [
      'Explain this repository and help me understand the codebase',
      'Analyze test coverage and add missing tests',
      'Create a detailed implementation plan for a feature',
    ],
    mode: {
      name: ModeSupportName.Agent,
      icon: AgentIcon,
      placeholder: 'Give Copilot a background task to work on',
      supportsModelSwitching: copilotFeatureFlags.codingAgentModelSelection,
      supportsReferenceAttachments: true,
      supportedReferenceTypes: ['image', 'file', 'snippet'] as const,
      supportsAttachmentsMenu: false,
    },
    customModelPicker: ({selectedAgent}: {selectedAgent?: SelectedAgent}) => (
      <SlashCommandModelPicker selectedAgent={selectedAgent} />
    ),
  }

  navigationItems: NavigationItem[] = [
    {
      id: AGENTS_PLUGIN_ID,
      displayName: 'Agents',
      href: AGENTS_PATH,
      icon: AgentIcon,
    },
  ]
}
